import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect, memo } from "react";

import SELECT_TYPES from "./location-select.constants";
import { getLanguage } from "../../helpers/language";
import { ReactComponent as TriangleArrowDown } from "../../assets/images/triangle_arrow_down.svg";

import useOutsideClick from "../../hooks/useOutsideClick";
import "./index.scss";

const LocationSelect = memo(
    forwardRef(
        (
            {
                onSelect,
                label = "",
                value = "",
                error = "",
                options = [],
                placeholder = "",
                styleHeight = "54px",
                type = SELECT_TYPES.WITHOUT_LABEL,
            },
            ref
        ) => {
            const { value: language } = getLanguage();
            const { t } = useTranslation();
            const selectRef = useRef();

            const [optionsList, setOptions] = useState(options);
            const [selected, setSelected] = useState(value);
            const [inputValue, setInputValue] = useState(value);
            const [isOpen, setOpen] = useState(false);

            const filteredOptionsByLanguage = optionsList.filter((item) => {
                const setSearchedItemLanguage = (lang) =>
                    item.nameTranslations?.[lang];

                const getTextInSearchedItem = (item) =>
                    item
                        ?.toLowerCase()
                        .includes(inputValue?.toLocaleLowerCase());

                return (
                    getTextInSearchedItem(setSearchedItemLanguage("en")) |
                    getTextInSearchedItem(setSearchedItemLanguage("ru")) |
                    getTextInSearchedItem(setSearchedItemLanguage("tr"))
                );
            });
            const proposedOptionsByLanguage = filteredOptionsByLanguage.map(
                (item) => item
            );

            const newFilteredArrayByLanguage = optionsList.filter((item) => {
                const checkTypeOption =
                    proposedOptionsByLanguage[
                        proposedOptionsByLanguage.indexOf(item)
                    ]?.name;

                return item.name !== checkTypeOption;
            });

            const filteredOptionsByLanguageList =
                filteredOptionsByLanguage.length >= 3 ||
                selected?.includes(inputValue)
                    ? [
                          ...filteredOptionsByLanguage,
                          ...newFilteredArrayByLanguage,
                      ]
                    : filteredOptionsByLanguage;

            useEffect(() => {
                setInputValue(value);
                setOptions(options);
                if (value !== selected) setSelected(value);
            }, [value, options]);

            const mapSelectLabel = (item) => {
                if (item?.label || item?.name) {
                    if (typeof item.label === "object")
                        return item.label[language];
                    else {
                        return t(item.label || item.name || item);
                    }
                }
                return t(item);
            };
            const checkSelectedItem = (item) => {
                if (type === SELECT_TYPES.WITHOUT_LABEL) {
                    if (item.name === value) return true;
                    if (selected === item) return true;
                    else return false;
                } else {
                    if (selected?.value === item?.value) return true;
                    else return false;
                }
            };

            const toggleDropdown = () => optionsList.length && setOpen(!isOpen);

            useOutsideClick(selectRef, () => setOpen(false));

            const setDropdownModifiers = () => {
                if (isOpen) {
                    return "-open";
                } else if (!optionsList.length && error) {
                    return "-error location-select__dropdown-disabled";
                } else if (value) {
                    return "-confirmed";
                } else if (error) {
                    return "-error";
                } else if (!optionsList.length) {
                    return "-disabled";
                }

                return "";
            };

            return (
                <div ref={selectRef || ref} className="location-select">
                    <div className="location-select__header">
                        {label && (
                            <label className="location-select__label">
                                {t(label)}
                            </label>
                        )}
                        <p className="location-select__error">
                            {error && t(error)}
                        </p>
                    </div>
                    <div
                        className={`location-select__dropdown${setDropdownModifiers()}`}
                    >
                        <div
                            style={{ height: styleHeight }}
                            className="location-select__dropdown_header"
                        >
                            <input
                                onClick={toggleDropdown}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={placeholder}
                                className="location-select__dropdown_header_input"
                            ></input>
                            <span
                                className={`location-select__dropdown_icon${
                                    isOpen ? "-reverted" : ""
                                }`}
                            >
                                <TriangleArrowDown onClick={toggleDropdown} />
                            </span>
                        </div>
                        <div
                            className={`location-select__dropdown_body ${
                                isOpen && "open"
                            }`}
                        >
                            {filteredOptionsByLanguageList.length > 0 ? (
                                filteredOptionsByLanguageList.map(
                                    (item, index) => (
                                        <div
                                            className="location-select__dropdown_inner"
                                            key={index}
                                            onClick={() => {
                                                onSelect(item);
                                                setInputValue(item.name);
                                                toggleDropdown();
                                            }}
                                        >
                                            <div
                                                className="location-select__dropdown_item"
                                                id={item.id}
                                            >
                                                <div
                                                    className={`location-select__dropdown_circle${
                                                        checkSelectedItem(item)
                                                            ? ""
                                                            : "-disable"
                                                    }`}
                                                >
                                                    {checkSelectedItem(
                                                        item
                                                    ) && (
                                                        <div className="location-select__dropdown_circle-border"></div>
                                                    )}
                                                </div>
                                                <div className="location-select__dropdown_item__label">
                                                    {item &&
                                                        mapSelectLabel(
                                                            item?.name
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                )
                            ) : (
                                <div className="location-select__dropdown_item">
                                    {t("nothing_found")}
                                </div>
                            )}
                        </div>
                        <div />
                    </div>
                </div>
            );
        }
    )
);

LocationSelect.displayName = "Select";
export default LocationSelect;
